/**
 * @file: ConstructionJobManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import {
  deleteCachedFile,
  readCachedFileContents,
  writeContentsToCachedFile,
} from '@euler/lib/cache/cacheUtil';
import { IdType } from '@euler/model/common';
import { CommonTaskStatus, InspectionTaskJobStatus } from '@euler/model/enum';
import {
  ConstructionJob,
  ConstructionJobCommitPayload,
  ConstructionJobMediaCommitPayload,
  ConstructionJobMediaPatch,
  ConstructionJobMediaPatchAdd,
  ConstructionJobMediaStaged,
  ConstructionJobProcedurePatch,
  ConstructionJobProcedureStaged,
  ConstructionJobProductConfirmationItemPayload,
  ConstructionJobProductConfirmationProcedureInfo,
  ConstructionJobProductProcedureApplyType,
  ConstructionJobStaged,
  PatchConstructionJobPayload,
  TaskProductManifestItemIdentifier,
  VersionedResponse,
} from '@euler/model/task-detail';
import { idEquals, isNotNull } from '@euler/utils';
import { array2map } from '@euler/utils/array';
import produce, { Immutable, freeze } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { BehaviorSubject, Subscription, debounceTime, skip } from 'rxjs';
import {
  MediaUploadTask,
  MediaUploadTaskEvent,
} from '../MediaFileUploadController';
import { TaskWorkflowNodeManager } from '../TaskWorkflowNodeManager';
import { IConstructionManager, TaskFileUploadRequestContext } from '../types';
import {
  constructionJobMediaToStaged,
  constructionJobToStaged,
  mediaStagedToCommitPayload,
} from '../util';

export class ConstructionJobManager extends TaskWorkflowNodeManager {
  readonly staged$: BehaviorSubject<ConstructionJobStaged>;
  readonly dirty$: BehaviorSubject<boolean>;

  private readonly cacheFilePath: string;
  private readonly removeFileUploadListener: () => void;
  private changeSubscription?: Subscription;
  private manifestChangeSubscription?: Subscription;
  private _disposed = false;

  constructor(
    private readonly constructionManager: IConstructionManager,
    private job: ConstructionJob,
    private readonly technicianId: number,
    private readonly technicianName: string,
  ) {
    super(constructionManager.taskManager);
    this.cacheFilePath = this.taskManager.getCacheFilePath(
      `construction/jobs/${job.id}.json`,
    );
    const staged = constructionJobToStaged(job, {
      technicianId,
      technicianName,
    });

    this.initialize(staged);

    this.staged$ = new BehaviorSubject<ConstructionJobStaged>(staged);
    this.dirty$ = new BehaviorSubject<boolean>(false);

    this.removeFileUploadListener =
      this.taskManager.uploadController.addListener((e, task) => {
        this.onFileUploadEvent(e, task as any);
      });

    this.startObserveChanges();
  }

  get disposed() {
    return this._disposed;
  }

  get jobName() {
    return this.job.name;
  }

  async loadCached(): Promise<ConstructionJobStaged | undefined> {
    const contents = await readCachedFileContents(this.cacheFilePath);
    if (contents == null) return undefined;
    return JSON.parse(contents) as ConstructionJobStaged;
  }

  applyCached(cached: ConstructionJobStaged) {
    this.withChangeObserverTemporarilyDisabled(() => {
      this.staged$.next(cached);
    });
  }

  async discardCache() {
    await deleteCachedFile(this.cacheFilePath, true);
  }

  reset() {
    this.resetWithConstructionJob(this.job);
  }

  async resetAndClearCache() {
    await this.discardCache();
    this.resetWithConstructionJob(this.job);
  }

  dispose() {
    this.removeFileUploadListener();
    this.stopObserveChanges();
    this._disposed = true;
  }

  async fetchMediasFromInspectionResults() {
    const medias =
      await this.taskService.getConstructionJobMediasFromInspectionResults(
        this.taskNo,
        this.job.id,
      );
    if (medias.length) {
      const procedure = this.staged$
        .getValue()
        .procedures.find(x => x.type === 'before');
      if (!procedure) return;
      this.withChangeObserverTemporarilyDisabled(() => {
        this.produceProcedure(procedure.name, draft => {
          draft.medias.unshift(
            ...medias.map(media => constructionJobMediaToStaged(media, true)),
          );
        });
      });
    }
  }

  removeMedia(procedure: string, key: string) {
    this.taskManager.uploadController.abort(key);
    this.produceProcedure(procedure, (item, original) => {
      const k = original.medias.findIndex(x => x._key === key);
      if (k < 0) {
        console.error(new Error('Cannot find target media, this must be bug'));
        return;
      }
      item.medias.splice(k, 1);
    });
  }

  findMedia(procedure: string, key: string) {
    const current = this.staged$
      .getValue()
      .procedures.find(x => x.name === procedure);
    return current?.medias.find(x => x._key === key);
  }

  initialize(staged: ConstructionJobStaged) {
    if (this.job.status !== InspectionTaskJobStatus.InProgress) {
      return;
    }

    if (staged.context.defaultProcedures == null) {
      // save default procedures for later restore purposes.
      staged.context.defaultProcedures = staged.procedures.map(x => ({
        name: x.name,
        type: x.type,
        removable: x.removable,
        allowChooseMediaFromGallery: x.allowChooseMediaFromGallery,
      }));
    }

    if (!staged.context.saved) {
      const detail = this.taskManager.cachedDetail;
      const confirmationItems = staged.context.productConfirmation?.items ?? [];
      const manifestItems = detail.productManifest?.items.filter(
        x => x.subject === this.job.name,
      );

      for (const confirmationItem of confirmationItems) {
        const manifestItem = manifestItems?.find(
          x =>
            idEquals(x.product.sourceContractId, confirmationItem.contractId) &&
            idEquals(x.product.id, confirmationItem.productId),
        );
        this.applyProductProcedures(
          staged,
          {
            ...confirmationItem,
            adopted: manifestItem != null && confirmationItem.adopted === true,
          },
          false,
        );
      }
    }
  }

  async confirmProductAdoption(
    confirmationItem: ConstructionJobProductConfirmationItemPayload,
  ) {
    const jobId = this.job.id;
    const hasEverSaved = this.job.context?.saved;
    const resp = await this.taskService.confirmConstructionJobProductAdoption(
      this.taskNo,
      jobId,
      confirmationItem,
    );
    this.constructionManager.handleVersionedResponse(
      resp,
      (_, { construction, original, response }) => {
        const index = original.jobs.findIndex(x => x.id === jobId);
        if (index < 0) {
          throw new Error(
            'Cannot find target construction job, this must be a bug',
          );
        }
        if (construction.status === CommonTaskStatus.Pending) {
          construction.status = CommonTaskStatus.InProgress;
        }
        const job = construction.jobs.find(x => x.id === jobId);
        if (job != null) {
          job.status = InspectionTaskJobStatus.InProgress;
          job.context = response.context;
          job.updatedAt = new Date();
        }
        this.taskManager.productManifestManager.produceManifest(draft => {
          const item = draft.items.find(
            x =>
              x.subject === this.jobName &&
              idEquals(
                x.product.sourceContractId,
                confirmationItem.contractId,
              ) &&
              idEquals(x.product.id, confirmationItem.productId),
          );
          if (item != null) {
            if (confirmationItem.adopted) {
              if (!item.jobRefs?.includes(jobId)) {
                if (!item.jobRefs) item.jobRefs = [];
                item.jobRefs?.push(jobId);
              }
            } else {
              if (item.jobRefs?.includes(jobId)) {
                item.jobRefs = item.jobRefs.filter(x => x !== jobId);
              }
            }

            if (
              confirmationItem.qty != null &&
              item.qty !== confirmationItem.qty
            ) {
              item.qty = confirmationItem.qty;
            }

            const resultConfirmationItem =
              response.context.productConfirmation?.items.find(x =>
                idEquals(x.productId, confirmationItem.productId),
              );
            if (resultConfirmationItem != null) {
              if (!item.confirmations) item.confirmations = {};
              item.confirmations[jobId] = resultConfirmationItem;
            }
          }
        });
      },
    );
    this.produce(draft => {
      draft.context = resp.response.context;
      this.applyProductProcedures(draft, confirmationItem, hasEverSaved);
    });
  }

  applyProductProcedures(
    job: ConstructionJobStaged,
    confirmationItem: {
      adopted: boolean;
      productId: IdType;
      procedures?: ConstructionJobProductConfirmationProcedureInfo[];
      proceduresRequired?: boolean;
      proceduresAppliedAs?: ConstructionJobProductProcedureApplyType;
    },
    ignoreDefaultProcedures: boolean | undefined,
  ) {
    if (!confirmationItem.procedures?.length) {
      return;
    }

    const map = array2map(confirmationItem.procedures, x => x.name);

    const existingProcedures = freeze(job.procedures, true);
    const applyType: ConstructionJobProductProcedureApplyType =
      confirmationItem.proceduresAppliedAs ?? 'merge';

    const results: ConstructionJobProcedureStaged[] = [];

    // always respect the inspection result procedure, and note that
    // inspection result procedure always comes at first by default.
    const inspectionResultProcedure = existingProcedures.find(
      x => x.type === 'before',
    );
    inspectionResultProcedure && results.push(inspectionResultProcedure);

    if (confirmationItem.adopted && applyType !== 'ignore') {
      //#region merge or replace recommended procedures.

      for (const recommendedProcedure of confirmationItem.procedures) {
        const existingProc = existingProcedures.find(
          x => x.name === recommendedProcedure.name,
        );
        // prevent duplicate procedures for inspection result procedure.
        if (inspectionResultProcedure?.name === recommendedProcedure.name) {
          continue;
        }
        results.push({
          name: recommendedProcedure.name,
          type: 'prod',
          medias: existingProc?.medias ?? [],
          removable:
            recommendedProcedure.removable === false ? false : undefined,
          allowChooseMediaFromGallery:
            existingProc?.allowChooseMediaFromGallery,
          stdImages: recommendedProcedure.stdImages,
        });
      }
      for (const existingProc of existingProcedures) {
        if (
          existingProc.type !== 'before' &&
          !map.has(existingProc.name) &&
          (applyType === 'merge' ||
            (applyType === 'replace' &&
              (existingProc.medias.length > 0 || existingProc.type === 'prod')))
        ) {
          results.push({
            ...existingProc,
          });
        }
      }
      //#endregion
    } else {
      //#region try to remove recommended procedures.
      for (const existingProc of existingProcedures) {
        if (
          existingProc.type !== 'before' && // non-inspection procedure
          (existingProc?.medias.length || // no medias uploaded
            !map.has(existingProc.name)) // is one of the recommended procedures
        ) {
          results.push(existingProc);
        }
      }
      //#endregion
    }

    // add default procedures if necessary.
    if (
      (!ignoreDefaultProcedures ||
        results.filter(x => x.type !== 'before').length === 0) &&
      job.context.defaultProcedures != null &&
      !job.context.productConfirmation?.items
        .filter(
          x =>
            confirmationItem.adopted ||
            !idEquals(x.productId, confirmationItem.productId),
        )
        .some(x => x.adopted && x.proceduresAppliedAs === 'replace')
    ) {
      for (const defaultProc of job.context.defaultProcedures) {
        if (!results.some(x => x.name === defaultProc.name)) {
          results.push({
            name: defaultProc.name,
            type: defaultProc.type ?? 'custom',
            medias: [],
            removable: defaultProc.removable,
            allowChooseMediaFromGallery:
              defaultProc.allowChooseMediaFromGallery,
          });
        }
      }
    }

    if (JSON.stringify(existingProcedures) !== JSON.stringify(results)) {
      job.procedures = results;
    }
  }

  async commit(params: {
    saveOnly: boolean;
    productRefs?: TaskProductManifestItemIdentifier[];
    label?: string;
    withPatching?: boolean;
  }) {
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let resp: VersionedResponse<ConstructionJob>;

    // eslint-disable-next-line @typescript-eslint/init-declarations
    let technicianId: number;
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let technicianName: string;

    if (params.withPatching) {
      const payload = this.getPatchPayload();

      payload.label = params.label;
      payload.finished = !params.saveOnly;
      payload.productRefs = params.productRefs;

      resp = await this.taskService.patchScheduledConstructionJob(
        this.taskNo,
        payload,
      );

      technicianId = payload.technicianId;
      technicianName = payload.technicianName;
    } else {
      const payload = this.getCommitPayload();

      payload.label = params.label;
      payload.saveOnly = params.saveOnly;
      payload.productRefs = params.productRefs;

      resp = await this.taskService.commitScheduledConstructionJob(
        this.taskNo,
        payload,
      );

      technicianId = payload.technicianId;
      technicianName = payload.technicianName;
    }

    this.constructionManager.handleVersionedResponse(
      resp,
      (_, { construction, original, response }) => {
        const index = original.jobs.findIndex(x => x.id === this.job.id);
        if (index < 0) {
          throw new Error(
            'Cannot find target construction job, this must be a bug',
          );
        }
        const job = (construction.jobs[index] = response);
        if (construction.status === CommonTaskStatus.Pending) {
          construction.status = CommonTaskStatus.InProgress;
        }
        if (job.technicianId == null || job.technicianName == null) {
          job.technicianId = technicianId;
          job.technicianName = technicianName;
        }
        this.job = job;
      },
    );
    await this.discardCache();
    this.resetWithConstructionJob(resp.response);
  }

  produce(
    recipe: (
      draft: WritableDraft<ConstructionJobStaged>,
      original: ConstructionJobStaged,
    ) => void,
  ) {
    const current = freeze(this.staged$.getValue());
    const nextState = produce(current, draft => {
      recipe(draft, current);
    });
    if (nextState !== current) {
      this.staged$.next(nextState);
    }
  }

  produceProcedure(
    name: string,
    recipe: (
      draft: WritableDraft<ConstructionJobProcedureStaged>,
      original: ConstructionJobProcedureStaged,
    ) => void,
    options?: { ignoreError?: boolean },
  ) {
    this.produce((draft, original) => {
      const index = original.procedures.findIndex(x => x.name === name);
      if (index < 0) {
        if (options?.ignoreError) return;
        throw new Error(
          'Cannot find construction job procedure, this must be a bug',
        );
      }
      recipe(draft.procedures[index], original.procedures[index]);
    });
  }

  private onFileUploadEvent(
    e: MediaUploadTaskEvent,
    task: Immutable<MediaUploadTask<TaskFileUploadRequestContext>>,
  ) {
    const request = task.request;
    const context = request.context;

    // ignore unwanted event that is not specific to me.
    if (
      context == null ||
      context.type !== 'construction-job' ||
      context.jobId !== this.job.id
    ) {
      return;
    }

    if (e.type === 'queued') {
      // add a new media state when queued.
      this.produceProcedure(context.procedure, (draft, original) => {
        if (context.key) {
          const index = original.medias.findIndex(x => x._key === context.key);
          if (index >= 0) {
            const media = draft.medias[index];
            media.annotationMetadata = context.annotationMetadata as any;
            media._source = {
              type: 'local',
              localFileUri: request.localFileUri,
              contentType: request.contentType,
              uploadParameters: request.parameters,
              state: { status: 'queued' },
            };
            return;
          }
        }

        draft.medias.push({
          type: request.contentType,
          annotationMetadata: context.annotationMetadata as any,
          _key: context.key ?? task.key,
          _timestamp: Date.now(),
          _source: {
            type: 'local',
            localFileUri: request.localFileUri,
            contentType: request.contentType,
            uploadParameters: request.parameters,
            state: { status: 'queued' },
          },
        });
      });
    } else {
      // update the existing media state.
      this.produceProcedure(context.procedure, (draft, original) => {
        // use original item to speed up searching
        const index = original.medias.findIndex(
          x => x._key === (context.key ?? task.key),
        );
        if (index < 0) return;
        const media = draft.medias[index];
        if (media._source.type !== 'local') return;
        switch (e.type) {
          case 'compressing': {
            media._source.state = {
              status: 'compressing',
              progress: e.progress,
            };
            return;
          }
          case 'begin': {
            media._source.state = { status: 'uploading', progress: 0 };
            return;
          }
          case 'progress': {
            media._source.state = {
              status: 'uploading',
              progress: e.progress,
            };
            return;
          }
          case 'error': {
            media._source.state = { status: 'error', error: e.error };
            return;
          }
          case 'abort': {
            if (task.status !== 'aborted') {
              media._source.state = {
                status: 'error',
                error: e.error ?? new Error('File uploading has aborted'),
              };
            }
            return;
          }
          case 'finished': {
            const cover = e.result.extra?.cover;
            media.coverUrl = cover != null && cover !== '' ? cover : undefined;
            media._source = {
              type: 'remote',
              url: e.result.url,
              cid: e.result.name,
            };
            return;
          }
          default:
            return;
        }
      });
    }
  }

  private getCommitPayload(): ConstructionJobCommitPayload {
    const staged = this.staged$.getValue();
    const payload: ConstructionJobCommitPayload = {
      id: this.job.id,
      commitId: this.job.commitId,
      technicianId: staged.technicianId,
      technicianName: staged.technicianName,
      category: staged.context?.category,
      remark: staged.remark,
      resolved: staged.context?.resolved,
      defaultProcedures: staged.context?.defaultProcedures,
      procedures: staged.procedures.map(procedure => ({
        name: procedure.name,
        type: procedure.type,
        removable: procedure.removable === false ? false : undefined,
        allowChooseMediaFromGallery:
          procedure.allowChooseMediaFromGallery === false ? false : undefined,
        medias: procedure.medias
          .map<ConstructionJobMediaCommitPayload | null>(media => {
            const obj = mediaStagedToCommitPayload(media);
            if (obj == null) return null;
            return {
              ...obj,
              detailId: media.detailId,
              tag: media.tag,
            };
          })
          .filter(isNotNull),
      })),
    };
    return payload;
  }

  private getPatchPayload(): PatchConstructionJobPayload {
    const staged = this.staged$.getValue();

    const patches: ConstructionJobProcedurePatch[] = [];

    const mediaStagedToPatch = (
      media: ConstructionJobMediaStaged,
    ): ConstructionJobMediaPatchAdd | null => {
      const obj = mediaStagedToCommitPayload(media);
      if (obj == null) return null;
      return {
        operation: 'add',
        media: {
          ...obj,
          detailId: media.detailId,
          tag: media.tag,
        },
      };
    };

    staged.procedures.forEach((procedure, index) => {
      const existingProcedureIndex = this.job.procedures.findIndex(
        x => x.name === procedure.name,
      );
      if (existingProcedureIndex < 0) {
        // add new procedure.
        patches.push({
          operation: 'add',
          name: procedure.name,
          index,
          removable: procedure.removable,
          allowChooseMediaFromGallery: procedure.allowChooseMediaFromGallery,
          type: procedure.type,
          medias: procedure.medias
            .map<ConstructionJobMediaPatchAdd | null>(mediaStagedToPatch)
            .filter(isNotNull),
        });
      } else {
        const existingProcedure = this.job.procedures[existingProcedureIndex];
        const removable = procedure.removable === false ? false : undefined;
        const allowChooseMediaFromGallery =
          procedure.allowChooseMediaFromGallery === false ? false : undefined;

        const mediaPatches: ConstructionJobMediaPatch[] = [];

        // check if there're any updates of the medias.
        const stagedMediaPatches = procedure.medias
          .map(mediaStagedToPatch)
          .filter(isNotNull);

        // check for new medias
        for (const stagedMediaPatch of stagedMediaPatches) {
          const url = stagedMediaPatch.media.url;
          const existingMedia = existingProcedure.medias.find(
            x => x.url === url,
          );
          if (existingMedia == null) {
            mediaPatches.push(stagedMediaPatch);
          }
        }

        // check for removed medias
        for (const existingMedia of existingProcedure.medias) {
          const stagedMediaPatch = stagedMediaPatches.find(
            x => x.operation === 'add' && x.media.url === existingMedia.url,
          );
          if (stagedMediaPatch == null) {
            mediaPatches.push({
              operation: 'remove',
              id: existingMedia.id,
              url: existingMedia.url,
            });
          }
        }
        if (
          mediaPatches.length > 0 ||
          existingProcedureIndex !== index ||
          existingProcedure.type !== procedure.type ||
          ('removable' in existingProcedure &&
            existingProcedure.removable != removable) ||
          ('allowChooseMediaFromGallery' in existingProcedure &&
            existingProcedure.allowChooseMediaFromGallery !==
              allowChooseMediaFromGallery)
        ) {
          patches.push({
            operation: 'update',
            name: procedure.name,
            index,
            removable,
            allowChooseMediaFromGallery,
            type: procedure.type,
            medias: mediaPatches,
          });
        }
      }
    });

    for (const existingProcedure of this.job.procedures) {
      const procedure = staged.procedures.find(
        x => x.name === existingProcedure.name,
      );
      if (procedure == null) {
        patches.push({
          operation: 'remove',
          name: existingProcedure.name,
        });
        continue;
      }
    }

    const patchPayload: PatchConstructionJobPayload = {
      id: this.job.id,
      commitId: this.job.commitId,
      technicianId: staged.technicianId,
      technicianName: staged.technicianName,
      category: staged.context?.category,
      remark: staged.remark,
      resolved: staged.context?.resolved,
      defaultProcedures: staged.context?.defaultProcedures,
      procedures: patches,
    };

    return patchPayload;
  }

  private resetWithConstructionJob(job: ConstructionJob) {
    this.withChangeObserverTemporarilyDisabled(() => {
      const staged = constructionJobToStaged(job, {
        technicianId: this.technicianId,
        technicianName: this.technicianName,
      });
      this.staged$.next(staged);
      this.dirty$.next(false);
    });
  }

  private startObserveChanges() {
    this.changeSubscription = this.staged$
      .pipe(skip(1), debounceTime(500))
      .subscribe(staged => {
        if (!this.dirty$.getValue()) {
          this.dirty$.next(true);
        }
        void writeContentsToCachedFile(
          this.cacheFilePath,
          JSON.stringify(staged, null, 2),
        );
        __DEV__ && console.log('job info written to cache');
      });

    this.manifestChangeSubscription =
      this.taskManager.productManifestManager.staged$.subscribe(manifest => {
        this.produce(job => {
          const confirmationItems =
            job.context.productConfirmation?.items ?? [];
          const manifestItems = manifest.items.filter(
            x => x.subject === this.job.name && !x.removed,
          );

          const removed = new Set<string>();
          for (const confirmationItem of confirmationItems) {
            const manifestItem = manifestItems?.find(
              x =>
                idEquals(
                  x.product.sourceContractId,
                  confirmationItem.contractId,
                ) && idEquals(x.product.id, confirmationItem.productId),
            );
            if (manifestItem == null) {
              this.applyProductProcedures(
                job,
                {
                  ...confirmationItem,
                  adopted: false,
                },
                job.context.saved,
              );
              removed.add(
                `${confirmationItem.contractId}:${confirmationItem.productId}`,
              );
            }
          }
          if (removed.size > 0) {
            job.context.productConfirmation!.items = confirmationItems.filter(
              x => !removed.has(`${x.contractId}:${x.productId}`),
            );
          }
        });
      });
  }

  private stopObserveChanges() {
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = undefined;
    }
    if (this.manifestChangeSubscription) {
      this.manifestChangeSubscription.unsubscribe();
      this.manifestChangeSubscription = undefined;
    }
  }

  private withChangeObserverTemporarilyDisabled(fn: () => any): any {
    this.stopObserveChanges();
    const result = fn();
    this.startObserveChanges();
    return result;
  }
}
